import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { hoyChicago, formatFecha, formatHora } from "@/lib/format";
import { MOTIVO_PENDIENTE, faltaNombre } from "@/lib/constants";
import { Input } from "@/components/ui/input";
import { ContainerId } from "@/components/marca/ContainerId";
import { PageHeader, EmptyState, Iniciales } from "@/components/marca/Layout";
import { CalendarClock, CheckCircle2, XCircle } from "lucide-react";
import { t } from "@/lib/i18n";

export default function Jornadas() {
  const navigate = useNavigate();
  const [fecha, setFecha] = useState(hoyChicago());
  const [jornadas, setJornadas] = useState([]);

  const cargar = useCallback(async () => {
    const { data } = await api.get(`/jornadas?fecha=${fecha}`);
    setJornadas(data);
  }, [fecha]);

  useEffect(() => { cargar(); }, [cargar]);

  const hrs = (j) => {
    if (!j.inicio_at) return "—";
    const fin = j.cierre_at ? new Date(j.cierre_at) : null;
    if (!fin) return "—";
    const h = (fin - new Date(j.inicio_at)) / 3600000;
    return `${h.toFixed(1)} h`;
  };
  const pctDia = (iso) => {
    const parts = new Intl.DateTimeFormat("en-US", { hour: "numeric", minute: "numeric", hour12: false, timeZone: "America/Chicago" }).formatToParts(new Date(iso));
    const h = Number(parts.find((p) => p.type === "hour").value) % 24 + Number(parts.find((p) => p.type === "minute").value) / 60;
    return Math.min(100, Math.max(0, ((h - 5) / 16) * 100));
  };

  return (
    <div className="p-4 lg:p-8 space-y-5">
      <PageHeader titulo={t("Jornadas")} subtitulo={formatFecha(fecha)} acciones={<Input data-testid="jornadas-fecha" type="date" value={fecha} onChange={(e) => setFecha(e.target.value)} className="h-10 w-44" />} />

      {jornadas.length === 0 && <EmptyState icon={CalendarClock} texto={t("No hay jornadas para esta fecha")} />}

      <div className="space-y-4">
        {jornadas.map((j) => (
          <div key={j.id} data-testid={`jornada-${j.chofer_id}`} className="bg-white rounded-xl border border-line p-4 lg:p-5">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <h3 className="flex items-center gap-2 font-display font-semibold text-[18px] text-ink"><Iniciales nombre={j.chofer_nombre} />{j.chofer_nombre}</h3>
              <div className="flex items-center gap-4 text-sm">
                <span className="flex items-center gap-1 text-[#277A3F]"><CheckCircle2 className="h-4 w-4" />{t(" Inicio ")}<span className="font-mono">{formatHora(j.inicio_at)}</span></span>
                {j.cierre_at
                  ? <span className="flex items-center gap-1 text-ink-2"><CheckCircle2 className="h-4 w-4" />{t(" Cierre ")}<span className="font-mono">{formatHora(j.cierre_at)}</span> · <span className="font-mono">{hrs(j)}</span></span>
                  : <span className="flex items-center gap-1 text-[#B07A12]"><XCircle className="h-4 w-4" />{t(" Sin cerrar")}</span>}
              </div>
            </div>
            {j.inicio_at && (
              <div className="mt-4" data-testid={`jornada-linea-${j.chofer_id}`}>
                <div className="relative h-2 rounded-full bg-[#ECE8E0]">
                  <span className="absolute top-0 h-2 rounded-full bg-ink" style={{ left: `${pctDia(j.inicio_at)}%`, width: `${Math.max(1.5, (j.cierre_at ? pctDia(j.cierre_at) : pctDia(new Date().toISOString())) - pctDia(j.inicio_at))}%` }} />
                </div>
                <div className="flex justify-between mt-1 text-[11.5px] text-ink-3 font-mono"><span>{t("5 AM")}</span><span>{t("1 PM")}</span><span>{t("9 PM")}</span></div>
              </div>
            )}
            {j.pendientes_informados?.length > 0 && (
              <div className="mt-4">
                <p className="eyebrow mb-2">{t("Pendientes informados")}</p>
                <div className="flex flex-wrap gap-2">
                  {j.pendientes_informados.map((p, i) => (
                    <button key={i} onClick={() => p.carga_id && navigate(`/panel/cargas/${p.carga_id}`)} className="flex items-center gap-2 text-left text-sm bg-[#FBF5E6] border border-[#E9D9B0] rounded-full px-3 py-1.5">
                      <ContainerId numero={p.contenedor || "—"} size="sm" />
                      <span className="text-ink-2">{t("Falta {v0} · {v1}{v2}", { v0: faltaNombre(p.falta), v1: MOTIVO_PENDIENTE[p.motivo] || p.motivo, v2: p.texto ? ` — ${p.texto}` : "" })}</span>
                      <span className="text-xs text-ink-3 font-mono">{formatHora(p.at)}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
